import moment from "moment";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import { makeApiCall } from "src/api/apiRequests";
import { useUserdata } from "src/store/UserData";
import { PermissionCheck } from "src/helpers/PermissionCheck";
import * as ConfirmPopup from "../../../helpers/ConfirmPopup";
import SimpleBar from "simplebar-react";
import API_HEADERS from "src/api/apiConfig";
import API_PATHS from "src/api/apiPaths";
import FLASH_MESSAGES from "src/helpers/FlashMessages";
import ValidationMessage from "src/helpers/ValidationMessage";
import useSearchSubmit from "src/hooks/useSearchSubmit";
import PATHS from "src/routes/Paths";
import GroupParticipants from "./GroupParticipants";
import { useMessageNotificationCount } from "src/store/chat/MessageNotifications";
import Loading from "src/helpers/Loading";

interface ProductsProps {
  threadId: string | undefined;
  getChatList: () => void;
}

const ThreadMessages: React.FC<ProductsProps> = ({ threadId, getChatList }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollRef = useRef<any>(null);
  const textareaRef = useRef(null);
  const userData = useUserdata((state: any) => state.userData);

  const addMessagesCount = useMessageNotificationCount(
    (state: any) => state.addMessagesCount
  );
  const messagesCount = useMessageNotificationCount(
    (state: any) => state.messagesCount
  );
  const [message, setMessage] = useState("");
  const [messagesList, setMessagesList] = useState<any[]>([]);
  const [chatWith, setChatWith] = useState("");
  const [subject, setSubject] = useState("");
  const { page, setPage } = useSearchSubmit();
  const [type, setType] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [activeDropdownId, setActiveDropdownId] = useState<number | null>(null);
  const [validations, setValidations] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);

  const getMessagesOfThread = async () => {
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.threadMessages(Number(threadId)),
        "GET",
        API_HEADERS.authenticated
      );
      setMessagesList(response.messages);
      setSubject(response.subject);
      setType(response.type);
      setChatWith(response.chat_with);
      addMessagesCount(response.total_unread_messages);
      setLoading(false);
    } catch (error: any) {
      if (error.response.status === 403) {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
        navigate(-1);
      } else if (error.response.status === 404) {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
        navigate(-1);
      }
    }
  };

  useEffect(() => {
    if (threadId) {
      getMessagesOfThread();
    }
  }, [threadId, messagesCount]);

  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.getScrollElement();
      scrollElement.scrollTop = scrollElement.scrollHeight;
    }
  }, [messagesList]);

  const handleResize = () => {
    const textarea: any = textareaRef.current;

    if (textarea) {
      textarea.style.height = "auto";
      const newHeight = Math.min(textarea.scrollHeight, 90);
      textarea.style.height = `${newHeight}px`;
      if (textarea.scrollHeight > 90) {
        textarea.style.overflowY = "auto";
      } else {
        textarea.style.overflowY = "hidden";
      }
    }
  };

  useEffect(() => {
    handleResize();
  }, [message]);

  const sendMessage = async (e?: FormEvent<HTMLFormElement>): Promise<void> => {
    e?.preventDefault();
    if (btnLoading) {
      return;
    }
    setBtnLoading(true);
    const formData = {
      body: message,
      thread_id: threadId,
    };
    try {
      await makeApiCall<ResponseType>(
        API_PATHS.sendMessage,
        "POST",
        API_HEADERS.authenticated,
        formData
      );
      getMessagesOfThread();
      setMessage("");
      getChatList();
      setValidations({});
    } catch (error: any) {
      if (error.response.status === 422) {
        setValidations(error.response.data);
      } else {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const deleteMessage = (threadId: number, messageTitle: string) => {
    ConfirmPopup.deletePopup(
      t("delete_message_title"),
      t("delete_message_type"),
      messageTitle
    ).then(async (response: any) => {
      if (response.isConfirmed) {
        try {
          const response: any = await makeApiCall<ResponseType>(
            API_PATHS.deleteMessage(threadId),
            "DELETE",
            API_HEADERS.authenticated
          );
          FLASH_MESSAGES.successMsg(response.success);
          if (getMessagesOfThread.length === 1 && page > 1) {
            setPage(page - 1);
          } else {
            getMessagesOfThread();
          }
        } catch (error: any) {
          if (error.response.data.error) {
            FLASH_MESSAGES.errorMsg(error.response.data.error);
          }
        }
      }
    });
  };

  return (
    <>
      <div className="chat-conversation">
        {loading ? (
          <div className="my-auto text-center">
            <Loading />
          </div>
        ) : (
          <>
            <div className="head">
              <i
                className="icon slider-left-arrow  back-icon"
                onClick={() => navigate(PATHS.chat)}
              />
              {type === "group" ? (
                <span className="text">{subject ? subject : ""}</span>
              ) : (
                <span className="text">{chatWith ? chatWith : ""}</span>
              )}

              {type === "group" ? (
                <>
                  <button
                    className="button-unstyled"
                    onClick={() => setShowModal(true)}
                  >
                    <i
                      className="icon info-chat-icon"
                      style={{ backgroundColor: "#474d66" }}
                    />
                  </button>
                  {showModal && (
                    <GroupParticipants
                      onShowModal={showModal}
                      setShowModal={(e) => setShowModal(e)}
                    />
                  )}
                </>
              ) : (
                ""
              )}
            </div>
            <div className="body mb-4">
              <SimpleBar className="simplebar-chat" ref={scrollRef}>
                {messagesList.map((message: any) => (
                  <div
                    className={
                      userData.id === message.user_id
                        ? "message own"
                        : "message"
                    }
                    key={message.id}
                  >
                    <div className="texts" key={message.id}>
                      <span className="user-name">
                        {userData.id !== message.user_id
                          ? message.user
                            ? `${message.user.first_name} ${message.user.last_name}`
                            : ""
                          : t("my_message")}
                      </span>

                      <span className="message-body ">
                        {message.body ? message.body : ""}
                        {PermissionCheck("message-delete-in-thread") &&
                          userData.id === message.user_id && (
                            <Dropdown
                              isOpen={activeDropdownId === message.id}
                              toggle={() =>
                                setActiveDropdownId(
                                  message.id ? message.id : null
                                )
                              }
                            >
                              <DropdownToggle caret={false} tag="span">
                                <span
                                  className="d-inline-flex"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdownId(message.id);
                                  }}
                                >
                                  <i className="icon sidebar-arrow ms-auto" />
                                </span>
                              </DropdownToggle>
                              <DropdownMenu className="dropdown-menu-end">
                                <DropdownItem
                                  onClick={() =>
                                    deleteMessage(message.id, message.body)
                                  }
                                >
                                  <i className="icon delete-icon" />
                                  {t("delete_mesage_popup")}
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          )}
                      </span>
                      <span className="time">{`${moment(
                        message.created_at
                      ).format("hh:mm A")}`}</span>
                    </div>
                  </div>
                ))}
              </SimpleBar>
            </div>
            {PermissionCheck("message-send-in-thread") ? (
              <>
                <div className="bottom ">
                  <textarea
                    name="message"
                    ref={textareaRef}
                    className={
                      "form-input" + (validations.body ? " input-error" : "")
                    }
                    onChange={(e) => setMessage(e.target.value)}
                    value={message}
                    placeholder={t("create_new_conversation_enter_message")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                  />

                  {btnLoading ? (
                    <button type="button" className="button-blue has-icon">
                      <i className="icon spinner-icon" />
                      {t("new_conversation_sending_message")}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="button-blue "
                      onClick={() => sendMessage()}
                    >
                      {t("new_conversation_send_message")}
                    </button>
                  )}
                </div>

                <div className="validation-message">
                  {validations.body && (
                    <ValidationMessage message={validations.body[0]} />
                  )}
                </div>
              </>
            ) : (
              ""
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ThreadMessages;
